import { jwtDecode } from 'jwt-decode';

/**
 * Resolves the Keycloak authentication server URL:
 * - When running in the browser on a remote/production domain (non-localhost),
 *   never redirect or fetch from localhost/127.0.0.1. Keycloak is reverse-proxied at `${window.location.origin}/auth`.
 * - If an explicit non-localhost URL is configured, use it.
 * - In local development (localhost / 127.0.0.1), use VITE_KEYCLOAK_URL or fallback to http://localhost:8080.
 */
function resolveKeycloakUrl(): string {
  const envUrl = (import.meta.env.VITE_KEYCLOAK_URL || '').trim();
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (!isLocalhost) {
      if (!envUrl || envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
        return `${window.location.origin}/auth`;
      }
    }
  }
  return envUrl || 'http://localhost:8080';
}

/**
 * Resolves the Backend API base URL:
 * - When running in the browser on a remote/production domain (non-localhost),
 *   never point to localhost/127.0.0.1. Use window.location.origin so requests route through nginx reverse proxy /api.
 * - In local development, use VITE_API_BASE_URL or fallback to http://localhost:8071.
 */
function resolveApiBaseUrl(): string {
  const envUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (!isLocalhost) {
      if (!envUrl || envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
        return window.location.origin;
      }
    }
  }
  return envUrl || 'http://localhost:8071';
}

const KEYCLOAK_URL = resolveKeycloakUrl();
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'classifieds';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'classifieds-frontend';
const API_BASE_URL = resolveApiBaseUrl();
const GA4_ID = import.meta.env.VITE_GA4_ID || '';

const KEYCLOAK_REDIRECT_URI = typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '';

export const config = {
  keycloak: {
    url: KEYCLOAK_URL,
    realm: KEYCLOAK_REALM,
    clientId: KEYCLOAK_CLIENT_ID,
    redirectUri: KEYCLOAK_REDIRECT_URI,
    tokenUrl: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`,
    authUrl: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth`,
    logoutUrl: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`,
    googleIdpUrl: `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth?kc_idp_hint=google`,
  },
  api: {
    baseUrl: API_BASE_URL,
    timeout: 30000,
  },
  analytics: {
    gaId: GA4_ID,
  },
};

export const getTokenFromStorage = (): string | null => {
  return localStorage.getItem('accessToken');
};

export const saveTokenToStorage = (token: string): void => {
  localStorage.setItem('accessToken', token);
};

export const removeTokenFromStorage = (): void => {
  localStorage.removeItem('accessToken');
};

export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    return !decoded.exp || decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const getDecodedToken = (token: string | null) => {
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

